/**
 * Règle métier : un document source (BL / commande / bon de réception) ne doit
 * être facturé QU'UNE fois.
 *
 * Le cas qui a motivé ce module : le poste A, hors-ligne, transforme BL-0007 en
 * facture ; pendant ce temps le poste B, en ligne, regroupe BL-0007 + BL-0008
 * dans une autre facture. À la synchronisation, rien ne s'y opposait — deux
 * factures sortaient pour la même livraison (double facturation du client), en
 * silence. `parent_doc_ids` n'a aucune contrainte d'unicité et `is_invoiced`
 * n'est qu'un drapeau posé après coup par le client.
 *
 * La détection est volontairement fondée sur l'EXISTENCE d'une facture enfant,
 * pas sur `is_invoiced` : le drapeau est écrit par une seconde requête après la
 * création (voir markAsInvoiced) et peut donc manquer alors que la facture,
 * elle, existe bien.
 */

/** Facture (vente ou achat) déjà présente côté serveur pour un parent donné. */
export interface ExistingChildInvoice {
  id: string;
  number: string;
  docDate: string;
  totalTtc: number;
  /** Parents couverts par cette facture — `parent_doc_id` inclus. */
  parentDocIds: string[];
  /**
   * Clé d'idempotence de la facture serveur, quand le backend en stocke une
   * (ventes). Sert à reconnaître NOTRE PROPRE facture lors d'un rejeu : une
   * tentative précédente peut avoir abouti côté serveur avant d'expirer côté
   * client. Sans ça, le rejeu prendrait sa propre facture pour un conflit.
   */
  clientRequestId?: string | null;
}

export interface ParentInvoicingCheckInput {
  /** Parents que la facture à créer prétend couvrir. */
  parentIds: string[];
  /** Factures existantes citant au moins un de ces parents. */
  existingInvoices: ExistingChildInvoice[];
  /** Clé d'idempotence de la facture qu'on s'apprête à créer (ventes). */
  clientRequestId?: string | null;
  /**
   * Numéro de la facture qu'on s'apprête à créer. Repli d'identification pour
   * les achats, qui n'ont pas de clé d'idempotence : le couple (fournisseur,
   * numéro) est unique, donc une facture portant ce numéro est forcément la
   * nôtre — un rejeu, pas un conflit.
   */
  ownNumber?: string | null;
}

export interface ParentInvoicingClash {
  /** Le parent déjà facturé. */
  parentId: string;
  /** La facture serveur qui le couvre déjà. */
  invoice: ExistingChildInvoice;
}

/**
 * `true` quand la facture serveur est en réalité celle que nous essayons de
 * créer (rejeu d'une tentative qui avait abouti côté serveur).
 */
export function isOwnInvoice(
  invoice: ExistingChildInvoice,
  ownKeys: { clientRequestId?: string | null; ownNumber?: string | null },
): boolean {
  const key = ownKeys.clientRequestId?.trim();
  if (key && invoice.clientRequestId && invoice.clientRequestId === key) return true;
  const num = ownKeys.ownNumber?.trim();
  if (num && invoice.number.trim() === num) return true;
  return false;
}

/**
 * Liste les parents déjà couverts par une AUTRE facture. Vide = la création
 * peut se faire sans arbitrage.
 *
 * Un même parent ne remonte qu'une fois (la première facture qui le cite) :
 * l'arbitrage porte sur « quelle facture garder », pas sur l'historique.
 */
export function findParentInvoicingClashes(
  input: ParentInvoicingCheckInput,
): ParentInvoicingClash[] {
  const wanted = new Set(input.parentIds.filter(Boolean));
  if (wanted.size === 0) return [];
  const clashes: ParentInvoicingClash[] = [];
  const seenParents = new Set<string>();
  for (const invoice of input.existingInvoices) {
    if (isOwnInvoice(invoice, input)) continue;
    for (const parentId of invoice.parentDocIds) {
      if (!wanted.has(parentId) || seenParents.has(parentId)) continue;
      seenParents.add(parentId);
      clashes.push({ parentId, invoice });
    }
  }
  return clashes;
}

/**
 * Factures serveur distinctes impliquées dans un lot de collisions — c'est ce
 * que le dialogue d'arbitrage affiche face à la facture hors-ligne.
 */
export function clashingInvoices(clashes: ParentInvoicingClash[]): ExistingChildInvoice[] {
  const byId = new Map<string, ExistingChildInvoice>();
  for (const clash of clashes) byId.set(clash.invoice.id, clash.invoice);
  return [...byId.values()];
}
